import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, ListChecks, Plus, StickyNote, Trash2 } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { storeLabel, type StoreOption } from './InventoryPage';

const PURCHASE_ORDERS_TAB = '/inventory?tab=purchase-orders';

interface OrderLine {
  key: number;
  productId: string;
  qty: string;
  unitCost: string;
}

let lineSequence = 0;
const nextLineKey = () => ++lineSequence;

export function NewPurchaseOrderPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), PURCHASE_ORDERS_TAB);
  const initialStoreId = params.get('storeId') ?? '';
  const [supplierId, setSupplierId] = useState('');
  const [storeId, setStoreId] = useState(initialStoreId);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [error, setError] = useState<string | null>(null);

  const stores = useQuery({
    queryKey: ['pharmacy-stores'],
    queryFn: () => get<StoreOption[]>('/pharmacy/stores'),
  });
  const suppliers = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => get<{ id: string; name: string }[]>('/pharmacy/suppliers?isActive=true'),
  });
  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => get<{ id: string; code: string; genericName: string; strength: string | null }[]>('/pharmacy/products?limit=200'),
  });

  const save = useMutation({
    mutationFn: () =>
      post('/pharmacy/purchase-orders', {
        supplierId,
        storeId,
        notes: notes || undefined,
        lines: lines.map((l) => ({
          productId: l.productId,
          qty: Number(l.qty),
          unitCostMinor: minorFromInput(l.unitCost),
        })),
      }),
    onSuccess: () => {
      toast.success(tr('pharmacy', 'purchase_order_raised'));
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const complete =
    Boolean(supplierId) &&
    Boolean(storeId) &&
    lines.length > 0 &&
    lines.every((l) => l.productId && Number(l.qty) > 0 && l.unitCost.trim() !== '' && minorFromInput(l.unitCost) >= 0);
  const dirty = !save.isSuccess && (supplierId !== '' || storeId !== initialStoreId || notes !== '' || lines.length > 0);

  return (
    <FormScreen
      title={tr('pharmacy', 'raise_a_purchase_order')}
      eyebrow={tr('pharmacy', 'inventory')}
      icon={ClipboardList}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!complete) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('ipd', 'order')} icon={ClipboardList}>
        <Field label={tr('pharmacy', 'supplier')} htmlFor="po-supplier" required hint={suppliers.data && suppliers.data.length === 0 ? tr('pharmacy', 'no_active_supplier_add_or_reactivate_one_on_the') : undefined}>
          <Select id="po-supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">{tr('pharmacy', 'select')}</option>
            {suppliers.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('pharmacy', 'store')} htmlFor="po-store" required>
          <Select id="po-store" value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            <option value="">{tr('pharmacy', 'select')}</option>
            {stores.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {storeLabel(s)}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('pharmacy', 'items')} icon={ListChecks} columns={1}>
        {lines.map((line, i) => (
          <div key={line.key} className="grid grid-cols-[1fr_90px_110px_32px] gap-2">
            <Select value={line.productId} onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, productId: e.target.value } : l)))}>
              <option value="">{tr('pharmacy', 'product_2')}</option>
              {products.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.genericName} {p.strength ?? ''} ({p.code})
                </option>
              ))}
            </Select>
            <Input
              type="number"
              min={0}
              placeholder={tr('pharmacy', 'qty_2')}
              value={line.qty}
              onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, qty: e.target.value } : l)))}
            />
            <Input
              type="text"
              dir="ltr"
              inputMode="decimal"
              placeholder={tr('pharmacy', 'cost_unit')}
              value={line.unitCost}
              onChange={(e) => setLines(lines.map((l, j) => (j === i ? { ...l, unitCost: e.target.value } : l)))}
            />
            <Button type="button" variant="ghost" size="icon" aria-label={tr('pharmacy', 'remove')} onClick={() => setLines(lines.filter((_, j) => j !== i))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => setLines([...lines, { key: nextLineKey(), productId: '', qty: '', unitCost: '' }])}>
            <Plus className="h-4 w-4" /> {tr('pharmacy', 'add_line')}
          </Button>
        </div>
      </FormSection>

      <FormSection title={tr('pharmacy', 'notes')} icon={StickyNote} columns={1}>
        <Field label={tr('pharmacy', 'notes')} htmlFor="po-notes">
          <Textarea id="po-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('pharmacy', 'raise_order')} loading={save.isPending} incomplete={!complete} cancelTo={returnTo} />
    </FormScreen>
  );
}
